import React, { useMemo } from 'react';

import { ClipboardButton, Flex, LssProp, Text } from '@lucidhq/lucidium';

import { useFetchAccounts } from '~/components/Shared/hooks/useFetchAccounts';
import { useFetchCountryLanguages } from '~/components/Shared/hooks/useFetchCountryLanguages';
import { t } from '~/utils/i18n';

import { useCampaignManager } from '../../CampaignManagerProvider';
import { Colors, ReviewLayout } from '../Misc/ReviewLayout';

const getDate = (date: string) => date.substring(0, 10).replace(/-/g, '/');

const headingLss: LssProp = {
  fontSize: '1.1rem',
  fontWeight: 'bold',
  mb: 'sm'
};

const valueLss: LssProp = {
  fontSize: '1.1rem'
};

export const ConnectCampaignBlock = () => {
  const { managedCampaign, goToStep, steps, can } = useCampaignManager();
  const { data: languages } = useFetchCountryLanguages();
  const { data: accounts } = useFetchAccounts({ isEnabled: true });

  const language = useMemo(() => {
    if (!languages) return null;
    const item = languages.items.find(
      lang => lang.id === managedCampaign?.details.country_language_id
    );

    return item;
  }, [languages, managedCampaign]);

  const account = useMemo(() => {
    if (!accounts) return null;
    const item = accounts?.items.find(
      acc => acc.permission_id === managedCampaign?.details.permission_id
    );

    return item;
  }, [accounts, managedCampaign]);

  return (
    <ReviewLayout.Content color={Colors.BLUE}>
      {(can.edit || can.limitedEdit) && (
        <ReviewLayout.Edit onClick={() => goToStep(steps.DETAILS.id)} />
      )}
      <ReviewLayout.Item>
        <Text lss={headingLss}>
          {t('campaignManager.review.campaign.items.studyId')}
        </Text>

        <Flex lss={{ alignItems: 'center' }}>
          <Text lss={valueLss}>{managedCampaign?.details.campaign_id}</Text>
          <ClipboardButton
            buttonText={t('campaignManager.review.campaign.clipboard.label')}
            copyText={managedCampaign?.details.campaign_id}
            iconPosition="left"
            showIcon
            tooltipText={t('campaignManager.review.campaign.clipboard.tooltip')}
            emphasis="low"
            lss={{
              display: 'flex',
              padding: '.25rem',
              marginLeft: '0.5rem'
            }}
          />
        </Flex>
      </ReviewLayout.Item>

      <ReviewLayout.Item>
        <Text lss={headingLss}>
          {t('campaignManager.review.campaign.items.studyName')}
        </Text>
        <Text lss={valueLss}>{managedCampaign?.details.campaign_name}</Text>
      </ReviewLayout.Item>

      <ReviewLayout.Item>
        <Text lss={headingLss}>
          {t('campaignManager.review.campaign.items.dateRange')}
        </Text>
        <Text lss={valueLss}>
          {managedCampaign?.details &&
            `${getDate(managedCampaign?.details.start_date)} - ${getDate(
              managedCampaign?.details.end_date
            )}`}
        </Text>
      </ReviewLayout.Item>

      <ReviewLayout.Item>
        <Text lss={headingLss}>
          {t('campaignManager.review.campaign.items.account')}
        </Text>
        <Text lss={valueLss}>
          {!!account?.group_name
            ? account.group_name
            : t('campaignManager.review.campaign.items.na')}
        </Text>
      </ReviewLayout.Item>

      <ReviewLayout.Item>
        <Text lss={headingLss}>
          {t('campaignManager.review.campaign.items.surveyLanguage')}
        </Text>
        <Text lss={valueLss}>
          {language?.country_name} - {language?.language_name}
        </Text>
      </ReviewLayout.Item>
    </ReviewLayout.Content>
  );
};
